import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, Lock, ArrowRight, Swords, Crown, Star, ShieldAlert, Check, Wallet } from "lucide-react";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";
import ChartEngine from "@/components/ChartEngine";
import { BossRetry, StageUnlocked } from "@/components/mission/Boss";

const EMOS = ["CALM", "FOCUSED", "CONFIDENT", "EXCITED", "NERVOUS", "FOMO", "FRUSTRATED", "IMPATIENT"];

export default function Boss4() {
  const navigate = useNavigate();
  const [phase, setPhase] = useState("loading");
  const [rs, setRs] = useState(null);       // round state
  const [reveal, setReveal] = useState(null);
  const [summary, setSummary] = useState(null);
  const [busy, setBusy] = useState(false);

  const [decision, setDecision] = useState("");
  const [risk, setRisk] = useState(1);
  const [entry, setEntry] = useState(""); const [stop, setStop] = useState(""); const [target, setTarget] = useState("");
  const [thesis, setThesis] = useState(""); const [inval, setInval] = useState("");
  const [conf, setConf] = useState(3);
  const [emoBefore, setEmoBefore] = useState(""); const [emoAfter, setEmoAfter] = useState("");

  useEffect(() => {
    api.post("/boss4/start", {}).then((r) => { setRs(r.data); primePlan(r.data); setPhase("round"); })
      .catch((e) => toast.error(apiErr(e)));
  }, []);

  const primePlan = (state) => {
    const c = state.candles?.[state.candles.length - 1]?.close;
    setDecision(""); setRisk(1); setThesis(""); setInval(""); setConf(3); setEmoBefore(""); setEmoAfter("");
    if (c) { setEntry(c); setStop(+(c * 0.99).toFixed(2)); setTarget(+(c * 1.02).toFixed(2)); }
  };

  const rr = (() => { const e = +entry, s = +stop, t = +target; if (!e || !s || !t) return null;
    const rk = decision === "SHORT" ? s - e : e - s; const rw = decision === "SHORT" ? e - t : t - e;
    return rk > 0 && rw > 0 ? +(rw / rk).toFixed(2) : null; })();

  const canLock = decision && (decision === "PASS" || (entry && stop && target && rr)) && emoBefore;

  const lock = async () => {
    if (!canLock) { toast.error("Choose a decision, a plan (if trading) and your emotional state."); return; }
    setBusy(true);
    try {
      const body = { session_id: rs.session_id, decision, risk_percent: +risk, thesis, invalidation: inval,
        confidence: conf, emotional_before: emoBefore, emotional_after: emoAfter };
      if (decision !== "PASS") { body.entry = +entry; body.stop = +stop; body.target = +target; }
      const { data } = await api.post("/boss4/lock", body);
      setReveal(data); setPhase("reveal");
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  const nextRound = async () => {
    if (reveal.done) {
      setBusy(true);
      try {
        const { data } = await api.post("/boss4/complete", { session_id: rs.session_id, hindsight: {} });
        setSummary(data); setPhase("summary");
      } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
    } else {
      setRs(reveal.next_round); primePlan(reveal.next_round); setReveal(null); setPhase("round");
    }
  };

  if (phase === "loading") return <div className="p-8 text-tc-muted font-mono text-sm">Preparing Boss Market #4…</div>;
  if (phase === "summary" && summary) return <Boss4Summary s={summary} navigate={navigate} />;

  if (phase === "reveal" && reveal) {
    const r = reveal.round_result;
    return (
      <div className="p-4 sm:p-6 lg:p-8 max-w-4xl mx-auto space-y-4" data-testid="boss4-reveal">
        <div className="flex items-center justify-between">
          <span className="text-[11px] font-mono uppercase tracking-widest text-tc-xp">Round Revealed</span>
          <span className="text-[12px] font-mono flex items-center gap-1.5 text-tc-sub"><Wallet className="w-4 h-4" /> ${r.account}</span>
        </div>
        <ChartEngine candles={r.revealed_candles} toggles={{ volume: true }} symbol={r.symbol} interval="5m" height={320} hideHeader
          overlays={r.decision !== "PASS" ? { entry: r.entry, stop: r.stop, target: r.target } : null} />
        <div className="tc-card p-4 flex items-center justify-between">
          <div>
            <div className="font-heading font-bold text-lg">{r.decision}{r.decision !== "PASS" ? ` · ${r.exit_reason}` : ""}</div>
            <div className="text-[12px] text-tc-muted">{r.category?.replace(/_/g, " ")} · setup: {r.setup_quality}</div>
          </div>
          <div className="text-right">
            {r.decision !== "PASS"
              ? <div className={`font-heading text-xl font-bold ${r.outcome_r >= 0 ? "text-tc-bull" : "text-tc-bear"}`} data-testid="boss4-round-r">{r.outcome_r >= 0 ? "+" : ""}{r.outcome_r}R</div>
              : <div className="text-tc-pass font-semibold">PASS · no capital risked</div>}
            {r.decision !== "PASS" && <div className="text-[12px] text-tc-muted">${r.pnl}</div>}
          </div>
        </div>
        <p className="text-[12px] text-tc-muted text-center">Outcome ≠ decision quality. Your process is scored at the end — not this single result.</p>
        <button data-testid="boss4-next-btn" onClick={nextRound} disabled={busy}
          className="w-full flex items-center justify-center gap-2 rounded-lg bg-tc-coach text-tc-bg font-bold py-3.5 hover:bg-tc-coach/90 disabled:opacity-50">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : null}{reveal.done ? "See Final Score" : "Next Round"} <ArrowRight className="w-4 h-4" />
        </button>
      </div>
    );
  }

  // ROUND (analyze)
  const meta = rs.round_meta;
  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-4xl mx-auto space-y-4" data-testid="boss4-round">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <span className="text-[11px] font-mono uppercase tracking-widest text-tc-xp flex items-center gap-1.5"><Swords className="w-4 h-4" /> BOSS #4 · ROUND {meta.round}/{meta.total}</span>
        <span className="text-[12px] font-mono flex items-center gap-1.5 text-tc-sub"><Wallet className="w-4 h-4" /> ${rs.account}</span>
      </div>
      <div className="flex gap-1.5">
        {[1, 2, 3, 4, 5].map((i) => <div key={i} className={`h-1.5 flex-1 rounded-full ${i < meta.round ? "bg-tc-bull" : i === meta.round ? "bg-tc-coach" : "bg-white/10"}`} />)}
      </div>
      <ChartEngine candles={rs.candles} toggles={{ volume: true }} symbol={rs.symbol} interval={rs.interval}
        overlays={decision && decision !== "PASS" ? { entry: +entry || null, stop: +stop || null, target: +target || null } : null} height={340} hideHeader />

      <div className="tc-card p-5 space-y-4">
        <div className="grid grid-cols-3 gap-2">
          {["LONG", "SHORT", "PASS"].map((d) => (
            <button key={d} data-testid={`boss4-decision-${d.toLowerCase()}`} onClick={() => setDecision(d)}
              className={`py-3 rounded-lg font-heading font-bold border ${decision === d ? (d === "LONG" ? "bg-tc-bull/20 text-tc-bull border-tc-bull/50" : d === "SHORT" ? "bg-tc-bear/20 text-tc-bear border-tc-bear/50" : "bg-tc-pass/20 text-tc-pass border-tc-pass/50") : "bg-white/5 text-tc-sub border-white/10 hover:border-white/30"}`}>{d}</button>
          ))}
        </div>

        <div>
          <div className="text-[10px] font-mono uppercase tracking-widest text-tc-muted mb-1.5">Risk Per Trade</div>
          <div className="flex gap-2 items-center">
            {[0.5, 1, 2].map((v) => (
              <button key={v} data-testid={`boss4-risk-${v}`} onClick={() => setRisk(v)}
                className={`px-3 py-1.5 rounded-lg text-xs font-mono ${risk === v ? "bg-tc-coach text-tc-bg" : "bg-white/5 text-tc-sub hover:text-tc-txt"}`}>{v}%</button>
            ))}
            <input data-testid="boss4-risk-custom" type="number" step="0.5" value={risk} onChange={(e) => setRisk(e.target.value)}
              className="w-20 bg-white/5 border border-white/10 rounded-md px-2 py-1.5 text-sm font-mono" />
            <span className="text-[11px] text-tc-muted">of ${rs.account} = ${(rs.account * risk / 100).toFixed(0)} risk</span>
          </div>
          {+risk > rs.max_risk_percent && <p className="text-[11px] text-tc-bear mt-1">Above the {rs.max_risk_percent}% training limit — this counts as oversized risk.</p>}
        </div>

        {decision && decision !== "PASS" && (
          <>
            <div className="grid sm:grid-cols-3 gap-3">
              {[["Entry", entry, setEntry, "entry"], ["Stop (invalidation)", stop, setStop, "stop"], ["Target", target, setTarget, "target"]].map(([l, v, set, t]) => (
                <div key={t}><div className="text-[10px] font-mono uppercase tracking-widest text-tc-muted mb-1">{l}</div>
                  <input data-testid={`boss4-${t}`} type="number" value={v} onChange={(e) => set(e.target.value)} className="w-full bg-white/5 border border-white/10 rounded-md px-3 py-2 text-sm font-mono" /></div>
              ))}
            </div>
            <div className="text-[12px] font-mono" data-testid="boss4-rr"><span className={rr ? (rr >= 2 ? "text-tc-bull" : rr >= 1.5 ? "text-tc-xp" : "text-tc-bear") : "text-tc-muted"}>R:R {rr ? `${rr}:1` : "—"}</span></div>
          </>
        )}

        <div className="grid sm:grid-cols-2 gap-3">
          <textarea data-testid="boss4-thesis" value={thesis} onChange={(e) => setThesis(e.target.value)} rows={2} placeholder="Thesis — why this decision?" className="w-full bg-white/5 border border-white/10 rounded-md px-3 py-2 text-sm" />
          <textarea data-testid="boss4-invalidation" value={inval} onChange={(e) => setInval(e.target.value)} rows={2} placeholder="My idea is wrong if…" className="w-full bg-white/5 border border-white/10 rounded-md px-3 py-2 text-sm" />
        </div>

        <div>
          <div className="text-[10px] font-mono uppercase tracking-widest text-tc-muted mb-1.5">How do you feel right now?</div>
          <div className="flex flex-wrap gap-1.5" data-testid="boss4-emotion-before">
            {EMOS.map((e) => <button key={e} data-testid={`boss4-emo-${e}`} onClick={() => setEmoBefore(e)} className={`px-2.5 py-1 rounded-md text-[11px] font-mono border ${emoBefore === e ? "bg-tc-coach/20 text-tc-coach border-tc-coach/50" : "bg-white/5 text-tc-muted border-white/10"}`}>{e}</button>)}
          </div>
        </div>

        <div>
          <div className="text-[10px] font-mono uppercase tracking-widest text-tc-muted mb-1.5">Confidence (1–5)</div>
          <div className="flex gap-2">{[1, 2, 3, 4, 5].map((n) => <button key={n} data-testid={`boss4-conf-${n}`} onClick={() => setConf(n)} className={`w-9 h-9 rounded-lg font-mono border ${conf === n ? "bg-tc-coach text-tc-bg border-tc-coach" : "bg-white/5 text-tc-sub border-white/10"}`}>{n}</button>)}</div>
        </div>

        <button data-testid="boss4-lock-btn" onClick={lock} disabled={busy || !canLock}
          className="w-full flex items-center justify-center gap-2 rounded-lg bg-tc-xp text-tc-bg font-bold py-3.5 hover:bg-tc-xp/90 disabled:opacity-50">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Lock className="w-4 h-4" />} LOCK PLAN & REVEAL
        </button>
      </div>
    </div>
  );
}

function Boss4Summary({ s, navigate }) {
  const labels = { risk_consistency: "Risk Consistency", position_sizing: "Position Sizing", invalidation_quality: "Invalidation Quality", trade_selection: "Trade Selection", execution_discipline: "Execution Discipline", emotional_discipline: "Emotional Discipline", reflection: "Reflection" };
  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-2xl mx-auto space-y-4" data-testid="boss4-summary">
      <div className="tc-card p-8 text-center">
        <div className="text-[11px] font-mono uppercase tracking-widest text-tc-muted">Boss Market #4 · Scored On Process</div>
        <div className="font-heading text-5xl font-extrabold mt-2" data-testid="boss4-score">{s.score}<span className="text-tc-muted text-2xl">/100</span></div>
        <div className="flex justify-center gap-2 mt-3">{[1, 2, 3].map((i) => <Star key={i} className={`w-8 h-8 ${i <= s.stars ? "text-tc-xp fill-tc-xp" : "text-white/15"}`} />)}</div>
        <div className={`text-sm mt-2 font-mono ${s.net_pnl >= 0 ? "text-tc-bull" : "text-tc-bear"}`}>Final balance ${s.final_balance} ({s.net_pnl >= 0 ? "+" : ""}${s.net_pnl})</div>
        <p className="text-tc-sub text-sm mt-2">{s.message}</p>
        {s.xp_awarded > 0 && <div className="font-mono text-tc-xp text-lg mt-2">+{s.xp_awarded} XP</div>}
      </div>

      <div className="tc-card p-5" data-testid="boss4-breakdown">
        <div className="text-[11px] font-mono uppercase tracking-widest text-tc-muted mb-3">Scorecard · P&L = 0 points</div>
        {Object.entries(s.breakdown).map(([k, v]) => (
          <div key={k} className="mb-2.5"><div className="flex justify-between text-[13px]"><span className="text-tc-sub">{labels[k] || k}</span><span className="font-mono">{v.score}/{v.max}</span></div>
            <div className="h-1.5 rounded-full bg-white/10 mt-1 overflow-hidden"><div className="h-full bg-tc-coach rounded-full" style={{ width: `${v.score / v.max * 100}%` }} /></div></div>
        ))}
      </div>

      {s.stage_complete && <StageUnlocked copy={s.stage_unlocked_copy} />}
      <BossRetry practice={s.practice} />

      {s.patterns?.length > 0 && (
        <div className="tc-card p-5"><div className="text-[11px] font-mono uppercase tracking-widest text-tc-muted mb-2">What Craft Coach Noticed</div>
          {s.patterns.map((p, i) => <div key={i} className="flex items-center gap-2 text-[13px] text-tc-sub mb-1">{["GOOD_PASS", "CONSISTENT_RISK", "GOOD_RECOVERY", "PROCESS_DISCIPLINE"].includes(p.code) ? <Check className="w-3.5 h-3.5 text-tc-bull" /> : <ShieldAlert className="w-3.5 h-3.5 text-tc-xp" />}{p.code.replace(/_/g, " ").toLowerCase()}</div>)}
        </div>
      )}

      <div className="grid grid-cols-2 gap-3">
        <button onClick={() => navigate("/training")} className="rounded-lg bg-white/5 border border-white/10 py-3 text-sm hover:bg-white/10">Trader Path</button>
        <button onClick={() => navigate("/risk-profile")} className="rounded-lg bg-tc-coach text-tc-bg font-semibold py-3 text-sm hover:bg-tc-coach/90">View Risk Profile</button>
      </div>
    </div>
  );
}
